/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Formik, Form, Field } from "formik";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import Button from "components/CustomButtons/Button.jsx";

import {
  btnFormSubmit,
  FormRadioButtonField,
  FormTextField,
  FormMultilineField,
  surveyValidation,
} from "components/Form/FormField";

import { addSurvey, clearSurvey, ApricotAction } from "client/actions/post";
import { ADD_SURVEY, SURVEY_FAILED } from "client/actions/types";
import { Modal } from "components/Modal/Modal";
import {
  sSubmit,
  sSurvQ1,
  sSurvQ2,
  sSurvCmt1,
  sSurvCmt2,
  sSurvCmt3,
  sSurveyOK,
  sSurveyFail,
  sGiveFeedback,
  sTryLater,
} from "assets/jss/views/ResStrings";
import { idSurvComment, idSubmit, idClose } from "assets/jss/views/ElementIDs";

import supPageStyle from "assets/jss/views/supportUs/supPageStyle";
const styles = createStyles(supPageStyle as any);

interface FormValues {
  Q1: string;
  Q2: string;
  // Q3: string;
  content: string;
  email: string;
}

interface Props extends WithStyles<typeof styles> {
  addSurvey: ApricotAction;
  clearSurvey: ApricotAction;
  // store
  user: any;
  surveyState: string;
  // props
  onClose: () => void;
}

const SurveyDialogBase: React.FC<Props> = ({
  addSurvey,
  clearSurvey,
  onClose,
  user,
  surveyState,
  classes,
}: Props) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const questions = [
    { desc: sSurvQ1, name: "Q1" },
    { desc: sSurvQ2, name: "Q2" },
    // { desc: sSurvQ3, name: "Q3" },
  ];

  useEffect(() => {
    return () => {
      clearSurvey();
    };
  }, []);

  useEffect(() => {
    if (surveyState) {
      setAlertModal(
        <Modal
          title={sGiveFeedback}
          color={surveyState === ADD_SURVEY ? "secondary" : "warning"}
          onExit={() => {
            setShowAlert(false);
            if (surveyState === ADD_SURVEY) {
              onClose();
            }
          }}>
          {surveyState === SURVEY_FAILED ? sSurveyFail : sSurveyOK}
        </Modal>,
      );
      setShowAlert(true);
    }
  }, [surveyState]);

  // Radio group
  const RadioButtonGroup = (props: any) => {
    const { handleChange, error, touched, children, desc } = props;
    return (
      <>
        <FormControl component="fieldset" style={{ width: "90%" }}>
          <FormLabel component="legend">{desc}</FormLabel>
          <RadioGroup
            aria-label="Choose for survey:"
            onChange={handleChange}
            children={children}
          />
        </FormControl>
        <br />
        {error && touched && (
          <span className={classes.inputError}>&bull;&nbsp;{error}</span>
        )}
      </>
    );
  };

  const renderQuestions = (props: any) => {
    const { values, errors, touched } = props;
    const choicesList = (name: string) => {
      let choices = [];
      for (let i: number = 5; i >= 1; i--) {
        choices.push(
          <Field
            key={i}
            component={FormRadioButtonField}
            name={name}
            id={i.toString()}
            label={i.toString()}
          />,
        );
      }
      return choices;
    };

    return (
      <>
        {questions.map((q: any, idx) => (
          <div style={{ marginTop: "0.5rem" }} key={idx}>
            <RadioButtonGroup
              id={q.name}
              value={values[q.name]}
              error={errors[q.name]}
              touched={touched[q.name]}
              desc={q.desc}
              classes={classes}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {choicesList(q.name)}
              </div>
            </RadioButtonGroup>
          </div>
        ))}
        <Divider style={{ margin: "1rem 0" }} />
      </>
    );
  };

  const renderComments = (values: FormValues) => {
    const limit = 800;
    return (
      <>
        <ul style={{ fontWeight: 400, color: "#0000008A" }}>
          <li>{sSurvCmt1}</li>
          <li>{sSurvCmt2}</li>
          <li>{sSurvCmt3}</li>
        </ul>
        <Field
          id={idSurvComment}
          name="content"
          boxclass={classes.mlTextBox}
          limit={limit}
          component={FormMultilineField}
        />
        <h6 style={{ float: "right", marginTop: 0 }}>
          {values.content.length}/{limit}
        </h6>
      </>
    );
  };

  const renderEmail = () =>
    user === false && (
      <Field
        name="email"
        type="email"
        component={FormTextField}
        label="Your email address (optional)"
      />
    );

  const dataToSend = (values: FormValues) => {
    let props = {};
    ["content", "email"].forEach((key: string) => {
      if (values[key] !== "") props[key] = values[key];
    });
    if (user) props["user"] = user._id;

    const { content, email, ...answers } = values;
    return { ...props, answers };
  };

  const surveyBody = (
    <Formik
      initialValues={{
        Q1: "",
        Q2: "",
        // Q3: "",
        content: "",
        email: "",
      }}
      onSubmit={(values: FormValues) => {
        if (surveyState === SURVEY_FAILED) {
          onClose();
        } else {
          addSurvey(dataToSend(values));
        }
      }}
      validationSchema={surveyValidation}
      render={(props) => {
        return (
          <Form>
            {renderQuestions(props)}
            {renderComments(props.values)}
            {renderEmail()}
            {surveyState === SURVEY_FAILED ? (
              <Button color="success" round onClick={onClose}>
                {sTryLater}
              </Button>
            ) : (
              btnFormSubmit({
                label: sSubmit,
                id: idSubmit,
                disabled: !props.isValid || props.isSubmitting,
              })
            )}
          </Form>
        );
      }}
    />
  );

  return (
    <>
      {showAlert && alertModal}
      <Dialog
        classes={{ paper: classes.surveyDlg }}
        fullScreen={fullScreen}
        open={true}
        onClose={onClose}
        disableBackdropClick={true}
        scroll={"paper"}>
        <DialogTitle
          classes={{ root: classes.dlgTitle }}
          style={{ backgroundColor: theme.palette.primary.main }}>
          <div className={classes.titleText}>
            {sGiveFeedback}
            <IconButton id={idClose} color="inherit" onClick={onClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>{surveyBody}</DialogContent>
      </Dialog>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    surveyState: state.state_posts.surveyState,
  };
}

const Base: any = SurveyDialogBase;
export const SurveyDialog: any = withStyles(styles)(
  connect(mapStateToProps, { addSurvey, clearSurvey })(Base),
);
